import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Compass, Zap, HelpCircle, Activity, Star, Eye, ShieldAlert, Crosshair } from "lucide-react";

interface StellarNavigationProps {
  language: "en" | "hr";
}

interface StarDetails {
  id: string;
  nameEn: string;
  nameHr: string;
  baseDistanceLy: number; // Base distance in Light Years
  baseVelocityKms: number; // Base radial velocity in km/s (positive means moving away / red-shifting)
  ra: string; // Right Ascension
  dec: string; // Declination
  frequencyHz: number;
  greekLetter: string;
  spectralType: string;
  x: number; // Position percentage X in HUD map (0-100)
  y: number; // Position percentage Y in HUD map (0-100)
  descriptionEn: string;
  descriptionHr: string;
}

const SEVEN_SISTERS_DATA: StarDetails[] = [
  {
    id: "alcyone",
    nameEn: "Alcyone",
    nameHr: "Alciona",
    baseDistanceLy: 440.35,
    baseVelocityKms: 10.1,
    ra: "03h 47m 29.1s",
    dec: "+24° 06' 18\"",
    frequencyHz: 432.09,
    greekLetter: "η Tauri",
    spectralType: "B7IIIe",
    x: 52,
    y: 50,
    descriptionEn: "The central and brightest star of the Pleiades cluster, a massive blue-white giant radiating high-harmonic coherence.",
    descriptionHr: "Središnja i najsjajnija zvijezda jata Plejada, masivni plavo-bijeli div koji zrači visokoharmoničnu koherentnost."
  },
  {
    id: "maia",
    nameEn: "Maia",
    nameHr: "Maja",
    baseDistanceLy: 360.22,
    baseVelocityKms: 7.2,
    ra: "03h 45m 49.6s",
    dec: "+24° 22' 04\"",
    frequencyHz: 435.50,
    greekLetter: "20 Tauri",
    spectralType: "B8III",
    x: 43,
    y: 35,
    descriptionEn: "A beautiful blue giant surrounded by a spectacular reflection nebula, acting as a high-frequency spiritual transmitter.",
    descriptionHr: "Prekrasan plavi div okružen spektakularnom refleksijskom maglicom, djeluje kao visokofrekventni duhovni odašiljač."
  },
  {
    id: "electra",
    nameEn: "Electra",
    nameHr: "Elektra",
    baseDistanceLy: 400.15,
    baseVelocityKms: 10.3,
    ra: "03h 44m 52.5s",
    dec: "+24° 06' 48\"",
    frequencyHz: 438.20,
    greekLetter: "17 Tauri",
    spectralType: "B6IIIe",
    x: 70,
    y: 45,
    descriptionEn: "An extremely fast-spinning blue giant near its breakup speed, radiating intense plasmic polar winds.",
    descriptionHr: "Izuzetno brzorotirajući plavi div blizu kritične brzine pucanja, zrači intenzivnim plazmatskim polarnim vjetrovima."
  },
  {
    id: "merope",
    nameEn: "Merope",
    nameHr: "Meropa",
    baseDistanceLy: 380.40,
    baseVelocityKms: 6.1,
    ra: "03h 46m 19.6s",
    dec: "+23° 56' 54\"",
    frequencyHz: 441.10,
    greekLetter: "23 Tauri",
    spectralType: "B6IVeV",
    x: 56,
    y: 65,
    descriptionEn: "Embedded within the Merope Nebula, this subgiant is shedding cosmic matter as it plows through interstellar gas.",
    descriptionHr: "Ugniježđen unutar maglice Meropa, ovaj poddiv odbacuje kozmičku tvar dok juri kroz međuzvjezdani plin."
  },
  {
    id: "taygeta",
    nameEn: "Taygeta",
    nameHr: "Tajgeta",
    baseDistanceLy: 410.80,
    baseVelocityKms: 5.9,
    ra: "03h 45m 12.5s",
    dec: "+24° 28' 02\"",
    frequencyHz: 444.00,
    greekLetter: "19 Tauri",
    spectralType: "B6IV",
    x: 32,
    y: 30,
    descriptionEn: "A spectroscopic triple-star system with a high-resonance energy signature that serves as Erra's stellar anchor point.",
    descriptionHr: "Spektroskopski sustav od tri zvijezde s visokorezonantnim energetskim potpisom koji služi kao zvjezdano uporište za planet Erra."
  },
  {
    id: "celaeno",
    nameEn: "Celaeno",
    nameHr: "Keleno",
    baseDistanceLy: 335.10,
    baseVelocityKms: 6.4,
    ra: "03h 44m 48.2s",
    dec: "+24° 17' 22\"",
    frequencyHz: 447.20,
    greekLetter: "16 Tauri",
    spectralType: "B7IV",
    x: 58,
    y: 28,
    descriptionEn: "Often called the 'Lost Pleiad' due to its moderate light density, carrying deep esoteric memory protocols.",
    descriptionHr: "Često nazivana 'izgubljenom Plejadom' zbog umjerene gustoće svjetla, nosi duboke ezoterične protokole pamćenja."
  },
  {
    id: "sterope",
    nameEn: "Sterope",
    nameHr: "Steropa",
    baseDistanceLy: 385.90,
    baseVelocityKms: 5.5,
    ra: "03h 45m 54.4s",
    dec: "+24° 33' 12\"",
    frequencyHz: 450.60,
    greekLetter: "21 Tauri",
    spectralType: "B8V",
    x: 25,
    y: 20,
    descriptionEn: "A hot, main-sequence blue dwarf paired visually with Asterope, serving as a dual-harmonic field stabilizer.",
    descriptionHr: "Vrući plavi patuljak glavnog niza, vizualno uparen s Asteropom, služi kao stabilizator dvostrukog harmoničkog polja."
  }
];

export default function StellarNavigation({ language }: StellarNavigationProps) {
  const [isActive, setIsActive] = useState(false);
  const [selectedStar, setSelectedStar] = useState<StarDetails>(SEVEN_SISTERS_DATA[0]);
  const [timeOffset, setTimeOffset] = useState(0);
  const [isAutoScanning, setIsAutoScanning] = useState(false);
  const autoScanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // High frequency update to drive real-time ticker values (micro-oscillations)
  useEffect(() => {
    let frameId: number;
    const updateTicker = () => {
      setTimeOffset(Date.now());
      frameId = requestAnimationFrame(updateTicker);
    };
    frameId = requestAnimationFrame(updateTicker);
    return () => cancelAnimationFrame(frameId);
  }, []);

  // Auto scanning loop
  useEffect(() => {
    if (isAutoScanning) {
      autoScanIntervalRef.current = setInterval(() => {
        setSelectedStar((prev) => {
          const currentIndex = SEVEN_SISTERS_DATA.findIndex((star) => star.id === prev.id);
          const nextIndex = (currentIndex + 1) % SEVEN_SISTERS_DATA.length;
          return SEVEN_SISTERS_DATA[nextIndex];
        });
      }, 4000);
    } else {
      if (autoScanIntervalRef.current) {
        clearInterval(autoScanIntervalRef.current);
      }
    }
    return () => {
      if (autoScanIntervalRef.current) {
        clearInterval(autoScanIntervalRef.current);
      }
    };
  }, [isAutoScanning]);

  // Compute Earth's orbital calculations and dynamic offsets
  // Based on current day of the year (slow drifts) + high speed micro oscillations
  const calculateTelemetry = (star: StarDetails) => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const diff = now.getTime() - startOfYear.getTime();
    const dayOfYear = diff / (1000 * 60 * 60 * 24);
    
    // Orbital angle of Earth (one cycle per year)
    const earthOrbitalAngleRad = (dayOfYear * 2 * Math.PI) / 365.25;
    
    // Each star has its own phase offset in RA/Dec
    const starOffsetRad = star.baseDistanceLy * 12.3;
    const totalAngle = earthOrbitalAngleRad + starOffsetRad;

    // Earth's maximum orbital shift is +/- 1 AU = +/- 1.581e-5 light years
    const maxDistanceShiftLy = 1.5812507e-5;
    const slowDistanceOffset = Math.sin(totalAngle) * maxDistanceShiftLy;
    
    // Micro jitter representing local plasma noise & orbital vibrations (for HUD look)
    // Simulating sub-millisecond distance fluctuations down to meters
    const fastOscillationPeriod = timeOffset / 1200;
    const microJitterLy = Math.sin(fastOscillationPeriod) * 1e-12 + Math.cos(fastOscillationPeriod * 2.3) * 3e-13;

    const currentDistanceLy = star.baseDistanceLy + slowDistanceOffset + microJitterLy;

    // Convert current distance light years to kilometers
    // 1 Light Year = 9,460,730,472,580.8 km
    const kmPerLy = 9460730472580.8;
    const currentDistanceKm = currentDistanceLy * kmPerLy;

    // Relative Velocity calculations
    // Earth's orbital speed is ~29.78 km/s
    // Projecting Earth's velocity vector onto the line of sight to the Pleiades
    const earthOrbitalVelocityKms = 29.78;
    const velocityProjection = Math.cos(totalAngle) * earthOrbitalVelocityKms;
    
    // High-speed micro-fluctuation representing Doppler acoustic resonance
    const microVelocityShift = Math.sin(timeOffset / 850) * 0.015 + Math.cos(timeOffset / 320) * 0.004;
    
    const currentVelocityKms = star.baseVelocityKms - velocityProjection + microVelocityShift;

    // Redshift (z) = Velocity / Speed of Light
    const speedOfLightKms = 299792.458;
    const redshiftZ = currentVelocityKms / speedOfLightKms;

    // Frequency shift based on Doppler formula
    // f_observed = f_emitted * sqrt((1 - beta) / (1 + beta)) where beta = v / c
    const beta = currentVelocityKms / speedOfLightKms;
    const dopplerRatio = Math.sqrt((1 - beta) / (1 + beta));
    const observedFrequencyHz = star.frequencyHz * dopplerRatio;

    return {
      distanceLy: currentDistanceLy,
      distanceKm: currentDistanceKm,
      velocityKms: currentVelocityKms,
      redshift: redshiftZ,
      resonanceHz: observedFrequencyHz,
      velocityShiftFactor: velocityProjection,
      approaching: currentVelocityKms < star.baseVelocityKms
    };
  };

  const telemetry = calculateTelemetry(selectedStar);

  return (
    <div className="absolute inset-0 pointer-events-none z-20 flex flex-col justify-between">
      {/* HUD Active Interface Header - Always visible with low opacity overlay button */}
      <div className="flex items-center justify-between p-3 md:p-4 bg-gradient-to-b from-cosmic-black/95 to-transparent w-full pointer-events-auto">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-quantum-turquoise animate-ping" : "bg-gray-500"}`}></div>
          <span className="font-mono text-[9px] md:text-[10px] text-quantum-turquoise tracking-widest uppercase">
            {isActive 
              ? (language === "en" ? "STELLAR SCANNER: ACTIVE" : "ZVJEZDANI SKENER: AKTIVAN")
              : (language === "en" ? "STELLAR SCANNER: STANDBY" : "ZVJEZDANI SKENER: U PRIPREMI")
            }
          </span>
        </div>

        <button
          onClick={() => {
            setIsActive(!isActive);
            if (!isActive) setIsAutoScanning(true); // turn on scan initially
          }}
          className={`px-3 py-1 font-mono text-[9px] md:text-[10px] tracking-wider uppercase border rounded transition-all cursor-pointer shadow-[0_0_12px_rgba(0,212,255,0.1)] flex items-center gap-1.5 ${
            isActive 
              ? "bg-quantum-turquoise/10 text-quantum-turquoise border-quantum-turquoise/50 hover:bg-quantum-turquoise/20" 
              : "bg-cosmic-black/85 text-celestial-light border-celestial-light/30 hover:border-quantum-turquoise/50 hover:text-quantum-turquoise hover:bg-quantum-turquoise/5"
          }`}
        >
          <Compass className={`w-3 h-3 ${isActive ? "animate-spin" : ""}`} />
          {isActive 
            ? (language === "en" ? "CLOSE SCANNER" : "ZATVORI SKENER")
            : (language === "en" ? "STELLAR NAVIGATION HUD" : "ZVJEZDANA NAVIGACIJA HUD")
          }
        </button>
      </div>

      {/* Main Full HUD Visualizer Overlay */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 bg-cosmic-black/95 backdrop-blur-[3px] border border-quantum-turquoise/40 flex flex-col pointer-events-auto overflow-hidden z-30"
          >
            {/* Dedicated Top Navigation Header with clear Close Button inside the HUD overlay */}
            <div className="flex items-center justify-between px-4 py-2 bg-cosmic-black border-b border-quantum-turquoise/30">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-ping"></span>
                <span className="font-mono text-[9px] md:text-[10px] text-quantum-turquoise tracking-widest uppercase font-bold">
                  {language === "en" ? "STELLAR NAVIGATION HUD // REAL-TIME PERSPECTIVE" : "ZVJEZDANA NAVIGACIJA HUD // PERSPEKTIVA U REALNOM VREMENU"}
                </span>
              </div>
              <button
                onClick={() => setIsActive(false)}
                className="px-3 py-1 font-mono text-[9px] md:text-[10px] tracking-wider uppercase border rounded cursor-pointer bg-red-950/20 hover:bg-red-950/40 text-red-400 border-red-500/30 hover:border-red-500/60 flex items-center gap-1.5 shadow-[0_0_8px_rgba(239,68,68,0.1)] transition-all"
              >
                <span className="text-sm font-bold leading-none">×</span>
                {language === "en" ? "CLOSE HUD" : "ZATVORI HUD"}
              </button>
            </div>

            {/* Content Columns Wrapper */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* HUD Left Column: Stellar Target Map */}
              <div className="flex-1 relative p-4 flex flex-col justify-between border-b md:border-b-0 md:border-r border-quantum-turquoise/20">
              {/* Star constellation map background design */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,212,255,0.06)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none opacity-40"></div>
              
              {/* Concentric orbital grids */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[150px] h-[150px] sm:w-[220px] sm:h-[220px] rounded-full border border-quantum-turquoise/10 border-dashed animate-[spin_100s_linear_infinite]"></div>
                <div className="w-[280px] h-[280px] sm:w-[380px] sm:h-[380px] rounded-full border border-quantum-turquoise/5 border-dotted animate-[spin_140s_linear_infinite_reverse]"></div>
                <div className="w-[80px] h-[80px] rounded-full border border-quantum-turquoise/15"></div>
              </div>

              {/* Header inside HUD map */}
              <div className="z-10 font-mono text-[9px] text-gray-400 uppercase tracking-widest flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Crosshair className="w-3 h-3 text-quantum-turquoise animate-pulse" />
                  {language === "en" ? "TARGET NAVIGATION SYSTEM // REAL-TIME PERSPECTIVE" : "NAVIGACIJSKI SUSTAV METE // PERSPEKTIVA U REALNOM VREMENU"}
                </span>
                <span className="text-quantum-turquoise hidden sm:inline">[M45 CONSTELLATION VECTOR]</span>
              </div>

              {/* Constellation Stars Grid Container */}
              <div className="relative flex-1 w-full min-h-[180px]">
                {/* SVG Connecting Astrological Vector Lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-25">
                  <g stroke="var(--quantum-turquoise)" strokeWidth="0.75" strokeDasharray="2,2">
                    {/* Alcyone to Maya */}
                    <line x1="52%" y1="50%" x2="43%" y2="35%" />
                    {/* Alcyone to Electra */}
                    <line x1="52%" y1="50%" x2="70%" y2="45%" />
                    {/* Alcyone to Merope */}
                    <line x1="52%" y1="50%" x2="56%" y2="65%" />
                    {/* Alcyone to Celaeno */}
                    <line x1="52%" y1="50%" x2="58%" y2="28%" />
                    {/* Maia to Taygeta */}
                    <line x1="43%" y1="35%" x2="32%" y2="30%" />
                    {/* Taygeta to Sterope */}
                    <line x1="32%" y1="30%" x2="25%" y2="20%" />
                    {/* Celaeno to Taygeta */}
                    <line x1="58%" y1="28%" x2="32%" y2="30%" />
                  </g>
                </svg>

                {/* Radar sweep visualization */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-quantum-turquoise/2 rounded-full pointer-events-none animate-pulse"></div>

                {/* Star nodes */}
                {SEVEN_SISTERS_DATA.map((star) => {
                  const isSelected = selectedStar.id === star.id;
                  return (
                    <button
                      key={star.id}
                      onClick={() => {
                        setSelectedStar(star);
                        setIsAutoScanning(false);
                      }}
                      style={{ left: `${star.x}%`, top: `${star.y}%` }}
                      className="absolute -translate-x-1/2 -translate-y-1/2 group/node p-2 focus:outline-none cursor-pointer"
                      aria-label={language === "en" ? `View details for star ${star.nameEn}` : `Prikaži detalje za zvijezdu ${star.nameHr}`}
                    >
                      {/* Interactive ring pulse */}
                      <span className={`absolute inset-0 rounded-full border border-quantum-turquoise/30 group-hover/node:scale-150 transition-transform duration-300 ${isSelected ? "animate-ping border-quantum-turquoise/60" : ""}`}></span>
                      
                      {/* Neon glowing center dot */}
                      <div className={`w-2.5 h-2.5 rounded-full relative z-10 transition-all ${
                        isSelected 
                          ? "bg-quantum-turquoise scale-125 shadow-[0_0_15px_#00d4ff]" 
                          : "bg-white/70 group-hover/node:bg-quantum-turquoise shadow-[0_0_6px_rgba(255,255,255,0.6)]"
                      }`}></div>
                      
                      {/* Name label */}
                      <span className={`absolute top-4 left-3 font-mono text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded transition-all whitespace-nowrap ${
                        isSelected 
                          ? "bg-quantum-turquoise/15 text-quantum-turquoise border border-quantum-turquoise/40 font-bold" 
                          : "bg-cosmic-black/80 text-gray-400 group-hover/node:text-white border border-transparent"
                      }`}>
                        {language === "en" ? star.nameEn : star.nameHr}
                        <span className="text-[7px] text-gray-500 ml-1">({star.greekLetter})</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* HUD Left Footer Controls */}
              <div className="z-10 flex items-center justify-between pt-2 border-t border-quantum-turquoise/10">
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsAutoScanning(!isAutoScanning)}
                    className={`px-2.5 py-1 font-mono text-[8px] uppercase tracking-wider border rounded cursor-pointer transition-colors ${
                      isAutoScanning 
                        ? "bg-quantum-turquoise/15 text-quantum-turquoise border-quantum-turquoise/30" 
                        : "bg-slate-900/60 text-gray-400 border-white/5 hover:text-white"
                    }`}
                  >
                    {language === "en" 
                      ? (isAutoScanning ? "■ STOP SCANNING" : "▶ AUTO-SCAN CYCLE")
                      : (isAutoScanning ? "■ ZAUSTAVI SKENIRANJE" : "▶ AUTOMATSKI CIKLUS")}
                  </button>
                </div>
                <div className="font-mono text-[8px] text-gray-500 flex items-center gap-1.5">
                  <Activity className="w-2.5 h-2.5 text-quantum-turquoise animate-pulse" />
                  <span>{language === "en" ? "GRID LOCK RATE: 100% COHERENT" : "UČESTALOST USKLAĐENOSTI REŠETKE: 100%"}</span>
                </div>
              </div>
            </div>

            {/* HUD Right Column: Active Telemetry Panel */}
            <div className="w-full md:w-[320px] lg:w-[360px] bg-cosmic-black/90 p-4 md:p-5 flex flex-col justify-between font-mono text-xs text-gray-300 border-t md:border-t-0 border-quantum-turquoise/20 overflow-y-auto">
              
              {/* Star Profile Header */}
              <div className="space-y-2 border-b border-white/10 pb-3">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-quantum-turquoise tracking-widest font-bold uppercase">{selectedStar.greekLetter}</span>
                  <span className="text-[8px] text-mystic-purple px-1.5 py-0.5 rounded bg-mystic-purple/10 border border-mystic-purple/20">
                    {selectedStar.spectralType}
                  </span>
                </div>
                <h5 className="text-base text-white font-display font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-quantum-turquoise animate-pulse" />
                  {language === "en" ? selectedStar.nameEn : selectedStar.nameHr}
                </h5>
                <p className="text-[10px] text-gray-400 leading-relaxed font-sans normal-case">
                  {language === "en" ? selectedStar.descriptionEn : selectedStar.descriptionHr}
                </p>
              </div>

              {/* Dynamic Telemetry Data Readout */}
              <div className="py-3 space-y-2.5">
                {/* Distance telemetry */}
                <div className="bg-white/2 p-2 rounded border border-white/5 space-y-1.5">
                  <div className="flex items-center justify-between text-[8px] text-gray-400 uppercase tracking-widest">
                    <span>{language === "en" ? "Earth Distance" : "Udaljenost od Zemlje"}</span>
                    <span className="text-quantum-turquoise animate-pulse">SCAN_TICK: LIVE</span>
                  </div>
                  <div className="space-y-1">
                    {/* Light Years down to microdecimals */}
                    <div className="text-base font-bold text-white tracking-widest flex items-end justify-between font-mono">
                      <span>{telemetry.distanceLy.toFixed(11)}</span>
                      <span className="text-[9px] text-quantum-turquoise ml-1 font-semibold">LY</span>
                    </div>
                    {/* Kilometer representation */}
                    <div className="text-[10px] text-gray-400 font-mono flex justify-between">
                      <span>≈ {Math.floor(telemetry.distanceKm).toLocaleString()} km</span>
                      <span className="text-[8px] opacity-60">
                        ({(telemetry.distanceLy * 63241.077).toFixed(2)} AU)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Relative Velocity Telemetry */}
                <div className="bg-white/2 p-2 rounded border border-white/5 space-y-1.5">
                  <div className="flex items-center justify-between text-[8px] text-gray-400 uppercase tracking-widest">
                    <span>{language === "en" ? "Relative Velocity" : "Relativna brzina"}</span>
                    <span className={`px-1 rounded text-[7px] font-bold ${telemetry.approaching ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border border-rose-500/20"}`}>
                      {telemetry.approaching 
                        ? (language === "en" ? "APPROACHING" : "PRIBLIŽAVANJE")
                        : (language === "en" ? "RECEDING" : "UDALJAVANJE")
                      }
                    </span>
                  </div>
                  <div className="flex items-end justify-between">
                    <div className="text-base font-bold text-white tracking-wider">
                      <span>{telemetry.velocityKms > 0 ? "+" : ""}{telemetry.velocityKms.toFixed(5)}</span>
                      <span className="text-[9px] text-pleiadian-blue ml-1.5">KM/S</span>
                    </div>
                    <div className="text-[9px] text-gray-500">
                      z = {telemetry.redshift.toFixed(8)}
                    </div>
                  </div>
                  {/* Doppler spectroscopy slide visualizer */}
                  <div className="pt-1 flex flex-col gap-1">
                    <div className="flex justify-between text-[7px] text-gray-500">
                      <span className="text-emerald-400 font-mono">BLUE-SHIFT (A)</span>
                      <span className="text-rose-500 font-mono">RED-SHIFT (B)</span>
                    </div>
                    <div className="w-full h-1 bg-white/5 rounded relative overflow-hidden">
                      {/* Doppler spectral mark bar */}
                      <div 
                        className={`absolute top-0 h-full w-2 rounded ${telemetry.approaching ? "bg-emerald-400 shadow-[0_0_6px_#34d399]" : "bg-rose-500 shadow-[0_0_6px_#f43f5e]"}`}
                        style={{ left: `${Math.max(0, Math.min(95, 50 + (telemetry.velocityShiftFactor / 29.78) * 45))}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Astrometry Data */}
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div className="p-1.5 bg-slate-950/40 rounded border border-white/5">
                    <span className="block text-[7px] text-gray-500 uppercase">Declination</span>
                    <span className="text-white font-bold">{selectedStar.dec}</span>
                  </div>
                  <div className="p-1.5 bg-slate-950/40 rounded border border-white/5">
                    <span className="block text-[7px] text-gray-500 uppercase">Right Ascension</span>
                    <span className="text-white font-bold">{selectedStar.ra}</span>
                  </div>
                </div>

                {/* Sound Harmonic Resonance Sync */}
                <div className="p-2 bg-gradient-to-r from-quantum-turquoise/5 to-mystic-purple/5 border border-quantum-turquoise/15 rounded space-y-1.5">
                  <div className="flex items-center justify-between text-[8px] text-quantum-turquoise uppercase tracking-wider">
                    <span className="flex items-center gap-1">
                      <Zap className="w-2.5 h-2.5 animate-pulse" />
                      {language === "en" ? "OBSERVED WAVE RESONANCE" : "PROMATRANA REZONANCIJA VALA"}
                    </span>
                    <span>HD_SNC</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-bold text-white">{telemetry.resonanceHz.toFixed(4)} <span className="text-[9px] text-gray-400 font-normal">Hz</span></span>
                    <span className="text-[8px] text-mystic-purple">({selectedStar.frequencyHz.toFixed(1)} Hz base)</span>
                  </div>
                </div>
              </div>

              {/* HUD Right Footer Section */}
              <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[8px] text-gray-500">
                <span className="flex items-center gap-1 text-emerald-400/80 uppercase">
                  <Eye className="w-3 h-3 animate-pulse" />
                  {language === "en" ? "SENSORY_TELEMETRY: OPTIMAL" : "SENZORNA_TELEMETRIJA: OPTIMALNA"}
                </span>
                <span>SYSTEM VERSION 5.1</span>
              </div>

            </div>
            </div> {/* End Content Columns Wrapper */}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
